import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SendIcon from '@mui/icons-material/Send';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SpeedIcon from '@mui/icons-material/Speed';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import SecurityIcon from '@mui/icons-material/Security';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import GAIG_LOGO from '../../../../assets/img/aftermarket-vendor-logos/gaig-logo.png';

export const InsuranceExpressLanding = () => {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      {/* Header Section */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Insurance Express
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          Integrated insurance inside Trnsact so you can provide quotes, place coverage, and deliver COIs without
          slowing down the deal
        </Typography>
        <Box className={classes.logoContainer}>
          <Typography variant="body2" color="textSecondary">
            Powered by Great American Insurance Group
          </Typography>
          <Box className={classes.logoWrapper}>
            <img src={GAIG_LOGO} alt="Great American Insurance Group" className={classes.logo} />
            <Box className={classes.divider} />
            <Typography variant="caption" className={classes.specialtyText}>
              Specialty Equipment
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Enable Section */}
      <Box className={classes.enableSection}>
        <Box className={classes.enableHeader}>
          <SecurityIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'none' }}>
              Enable Insurance Express to Get Started
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Insurance Express must be set up first. Complete the setup steps below to unlock these capabilities.
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3} className={classes.benefitsGrid}>
          <Grid item xs={12} md={4}>
            <Box className={classes.benefit}>
              <CheckCircleIcon color="primary" sx={{ fontSize: 32, mr: 2, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'none' }}>
                  Earn Commission on Policies
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Turn approved coverage into a consistent revenue stream
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box className={classes.benefit}>
              <RequestQuoteIcon color="primary" sx={{ fontSize: 32, mr: 2, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'none' }}>
                  Provide Instant Quotes
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Provide quote for coverage while customer is still engaged
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box className={classes.benefit}>
              <SendIcon color="primary" sx={{ fontSize: 32, mr: 2, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'none' }}>
                  COIs Delivered Directly
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Collect proof of insurance inside the same workflow as your deal
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Why Enable Section */}
      <Box className={classes.whySection}>
        <Typography variant="h5" className={classes.sectionTitle}>
          Why Enable Insurance Express?
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card className={classes.featureCard}>
              <CardContent className={classes.featureCardContent}>
                <IntegrationInstructionsIcon color="secondary" sx={{ fontSize: 32, mr: 2, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'none' }}>
                    Integrated Quotes
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Offer insurance quotes directly within your deals without leaving the platform
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.featureCard}>
              <CardContent className={classes.featureCardContent}>
                <SecurityIcon color="primary" sx={{ fontSize: 32, mr: 2, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'none' }}>
                    Automatic COIs
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Automatically receive certificates of insurance from accepted quotes
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.featureCard}>
              <CardContent className={classes.featureCardContent}>
                <SpeedIcon sx={{ fontSize: 32, mr: 2, flexShrink: 0, color: '#4caf50' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'none' }}>
                    Faster Turnaround
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Reduce delays and back-and-forth by keeping insurance communication and status in one place
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.featureCard}>
              <CardContent className={classes.featureCardContent}>
                <AttachMoneyIcon sx={{ fontSize: 32, mr: 2, flexShrink: 0, color: '#ff9800' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'none' }}>
                    Insurance Revenue
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Feel free on approved insurance policies directly through completed insurance quotes
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.featureCard}>
              <CardContent className={classes.featureCardContent}>
                <VerifiedUserIcon sx={{ fontSize: 32, mr: 2, flexShrink: 0, color: '#00bcd4' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'none' }}>
                    Lender Compliance
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Deliver certificates of insurance meet lender-specific requirements and delivery standards
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.featureCard}>
              <CardContent className={classes.featureCardContent}>
                <ThumbUpIcon sx={{ fontSize: 32, mr: 2, flexShrink: 0, color: '#9c27b0' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'none' }}>
                    Trusted Insurance Providers
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Offer insurance quotes sourced from trusted providers approved for use on the platform
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Get Started Section */}
      <Box className={classes.getStartedSection}>
        <Box className={classes.getStartedHeader}>
          <Box className={classes.rocketIcon}>🚀</Box>
          <Typography variant="h5" fontWeight="bold">
            Get Started with Insurance Express!
          </Typography>
        </Box>

        {/* Three summary cards */}
        <Grid container spacing={0} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card className={classes.summaryCardArrow}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: '#2e7d32', textTransform: 'none' }}
                >
                  Sign Up
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Complete applicable licensing and agreements to enable Insurance Express.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card className={classes.summaryCardArrow}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: '#2e7d32', textTransform: 'none' }}
                >
                  Configure Programs
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Add lenders and set insurance requirements so COIs flow automatically.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ color: '#2e7d32', textTransform: 'none' }}
                >
                  Start Quoting
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Provide quotes and deliver COIs inside deals without extra back and forth.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.setupCard}>
              <CardContent>
                <Box className={classes.setupHeader}>
                  <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'none' }}>
                    Sign Up to Sell Insurance
                  </Typography>
                  <Box className={classes.stepBadge}>Step 1</Box>
                </Box>

                <Box className={classes.setupSteps}>
                  <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'none' }}>
                    Get Insurance Express enabled, then start quoting coverage directly in your deals
                  </Typography>
                  <Box className={classes.stepItem}>
                    <CheckCircleIcon fontSize="small" color="primary" />
                    <Typography variant="body2">Complete applicable licensing and agreements</Typography>
                  </Box>
                  <Box className={classes.stepItem}>
                    <CheckCircleIcon fontSize="small" color="primary" />
                    <Typography variant="body2">Enable quoting inside deals</Typography>
                  </Box>
                  <Box className={classes.stepItem}>
                    <CheckCircleIcon fontSize="small" color="primary" />
                    <Typography variant="body2">Start earning commission on approved policies *</Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  className={classes.actionButton}
                  onClick={() => window.open('https://info.trnsact.com/insurance-express-get-started', '_blank')}
                >
                  SIGN UP NOW
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card className={classes.setupCard}>
              <CardContent>
                <Box className={classes.setupHeader}>
                  <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'none' }}>
                    Configure Lender Programs
                  </Typography>
                  <Box className={classes.stepBadge}>Step 2</Box>
                </Box>

                <Box className={classes.setupSteps}>
                  <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'none' }}>
                    After you sign up, add your lenders and set insurance requirements so quotes and COIs route
                    correctly.
                  </Typography>
                  <Box className={classes.stepItem}>
                    <CheckCircleIcon fontSize="small" color="primary" />
                    <Typography variant="body2">Add lenders and finance programs</Typography>
                  </Box>
                  <Box className={classes.stepItem}>
                    <CheckCircleIcon fontSize="small" color="primary" />
                    <Typography variant="body2">Configure each lender's insurance requirements</Typography>
                  </Box>
                </Box>

                <Button variant="outlined" color="primary" size="large" fullWidth className={classes.actionButton}>
                  VIEW SETUP GUIDE
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Footer Section */}
      <Card className={classes.footerCard}>
        <CardContent>
          <Box className={classes.footer}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textTransform: 'none' }}>
                Have Questions?
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Get answers on commission basics, and how lender requirements are configured for your workflow.
              </Typography>
            </Box>
            <Box className={classes.footerButtons}>
              <Button variant="outlined" color="primary" startIcon={<HelpOutlineIcon />}>
                LEARN MORE
              </Button>
              <Button variant="contained" color="primary" startIcon={<HeadsetMicIcon />} sx={{ ml: 2 }}>
                CONTACT SUPPORT
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Typography variant="caption" color="textSecondary" className={classes.disclaimer}>
        * In order to receive commission or other compensation for placing or servicing physical damage insurance
        required by law to hold and maintain a valid insurance Producer License in the applicable state.
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles({
  root: {
    backgroundColor: 'white',
    padding: '32px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  subtitle: {
    color: '#666',
    marginBottom: '16px',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    marginTop: '16px',
  },
  logoWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '12px',
  },
  logo: {
    height: '60px',
    objectFit: 'contain',
  },
  divider: {
    width: '1px',
    height: '25px',
    backgroundColor: '#d32f2f',
    alignSelf: 'flex-end',
  },
  specialtyText: {
    fontWeight: 'bold',
    fontSize: '11px',
    color: '#003366',
  },
  enableSection: {
    backgroundColor: '#E3F2FD',
    padding: '24px',
    borderRadius: '8px',
    marginBottom: '32px',
  },
  enableHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  benefitsGrid: {
    marginTop: '16px',
  },
  benefit: {
    display: 'flex',
    alignItems: 'flex-start',
    textAlign: 'left',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  whySection: {
    marginBottom: '32px',
  },
  sectionTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: '24px',
  },
  featureCard: {
    height: '100%',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'box-shadow 0.3s',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  featureCardContent: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  getStartedSection: {
    backgroundColor: '#F0FFF4',
    padding: '24px',
    borderRadius: '8px',
    marginBottom: '32px',
  },
  getStartedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  rocketIcon: {
    fontSize: '32px',
  },
  summaryCard: {
    height: '100%',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  summaryCardArrow: {
    height: '100%',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    position: 'relative',
    clipPath: 'polygon(0 0, calc(100% - 40px) 0, 100% 50%, calc(100% - 40px) 100%, 0 100%)',
    marginRight: '-10px',
  },
  setupCard: {
    height: '100%',
  },
  setupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    backgroundColor: '#E3F2FD',
    padding: '12px 16px',
    borderRadius: '8px',
    margin: '-16px -16px 16px -16px',
  },
  stepBadge: {
    backgroundColor: '#1976d2',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  setupSteps: {
    marginTop: '16px',
    marginBottom: '24px',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  actionButton: {
    marginTop: '16px',
  },
  footerCard: {
    backgroundColor: '#f5f5f5',
    marginTop: '32px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  footerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  disclaimer: {
    display: 'block',
    marginTop: '24px',
    fontStyle: 'italic',
    textAlign: 'center',
  },
});
